import React, { useState, useEffect, useCallback } from 'react';

const SecureDeleteSimulator = ({ deletionSuccess }) => {
  const calculateGridSize = () => {
    const cols = Math.floor(window.innerWidth / 20);
    const rows = Math.floor(window.innerHeight / 30);
    return {
      rows: Math.min(Math.max(10, rows), 30),
      cols: Math.min(Math.max(20, cols), 50)
    };
  };

  const generateGrid = (rows, cols) => {
    return Array(rows).fill().map(() =>
      Array(cols).fill().map(() => Math.random().toString(2).substring(2, 8).padStart(6, '0'))
    );
  };

  const generateData = (type) => {
    switch(type) {
      case 'hex':
        return Math.floor(Math.random() * 0xFFFFFF).toString(16).padStart(6, '0').toUpperCase();
      case 'binary':
        return Math.random().toString(2).substring(2, 8).padStart(6, '0');
      case 'ascii':
        const chars = '!@#$%^&*()_+-=[]{}|;:,.<>?♠♣♥♦☺☻♪♫★※→←↑↓';
        return Array(3).fill().map(() => chars[Math.floor(Math.random() * chars.length)]).join('');
      default:
        return '      ';
    }
  };

  const [stage, setStage] = useState('initializing');
  const [scanLine, setScanLine] = useState(0);
  const [displayMode, setDisplayMode] = useState('hex');
  const [errorOccurred, setErrorOccurred] = useState(false);
  const [progress, setProgress] = useState(0);
  const [startTime, setStartTime] = useState(null);
  const [currentTime, setCurrentTime] = useState(null);
  const [dimensions, setDimensions] = useState(calculateGridSize());
  const [grid, setGrid] = useState(generateGrid(dimensions.rows, dimensions.cols));
  const [status, setStatus] = useState('');

  // Constants
  //const BASE_PHASE_DURATION = 30000; // 30 seconds per phase
  // 30 minutes
  //const BASE_PHASE_DURATION = 1800000; // 1,800,000 milliseconds (30 minutes)

   // 24 hours
  const BASE_PHASE_DURATION = 86400000; // 86,400,000 milliseconds (24 hours)

  const ACCELERATED_PHASE_DURATION = 5000; // 5 seconds if server responds with success
  const SCAN_SPEED = 100; // Speed of scanning line (ms)
  const ERROR_PROBABILITY = 0.15;

  // Status messages for each phase
  const statusMessages = {
    hex: [
      "Initializing secure deletion protocol...",
      "Scanning memory blocks for sensitive data...",
      "Applying military-grade overwrite patterns...",
      "Implementing DoD 5220.22-M wiping standard...",
      "Verifying hexadecimal overwrite integrity..."
    ],
    binary: [
      "Initiating binary randomization sequence...",
      "Applying zero-one pattern overwrite...",
      "Implementing reverse binary patterns...",
      "Verifying bit-level data destruction...",
      "Running binary verification checks..."
    ],
    ascii: [
      "Applying final character-based overwrite...",
      "Implementing random ASCII pattern injection...",
      "Performing deep character substitution...",
      "Verifying complete data obliteration...",
      "Running final verification sequence..."
    ]
  };

  const updateScanLine = useCallback(() => {
    if (stage === 'completed') return;

    setScanLine(prev => (prev >= dimensions.rows - 1 ? 0 : prev + 1));
    const scanInterval = setInterval(() => {
      setScanLine(prev => (prev >= dimensions.rows - 1 ? 0 : prev + 1));
    }, SCAN_SPEED);
    
    return () => clearInterval(scanInterval);
  }, [stage, dimensions.rows]);

  const startPhase = useCallback((phase) => {
    setDisplayMode(phase);
    setStatus(statusMessages[phase][Math.floor(Math.random() * statusMessages[phase].length)]);
    
    const statusUpdateInterval = setInterval(() => {
      setStatus(statusMessages[phase][Math.floor(Math.random() * statusMessages[phase].length)]);
    }, 3000);

    return () => clearInterval(statusUpdateInterval);
  }, []);

  useEffect(() => {
    const handleResize = () => setDimensions(calculateGridSize());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (stage === 'initializing') {
      setStartTime(Date.now());
      setStage('running');
      startPhase('hex');
      updateScanLine();
    }
    return () => clearInterval(updateScanLine);
  }, [stage, startPhase, updateScanLine]);

  useEffect(() => {
    const timer = setInterval(() => {
      if (startTime && stage !== 'completed') {
        setCurrentTime(Date.now());

        const phaseDuration = deletionSuccess ? ACCELERATED_PHASE_DURATION : BASE_PHASE_DURATION;
        const totalDuration = phaseDuration * 3; // 3 phases
        const elapsed = Date.now() - startTime;
        const newProgress = Math.min((elapsed / totalDuration) * 100, 100);
        setProgress(newProgress);

        if (elapsed <= phaseDuration) {
          if (displayMode !== 'hex') startPhase('hex');
        } else if (elapsed <= phaseDuration * 2) {
          if (displayMode !== 'binary') startPhase('binary');
        } else if (elapsed <= phaseDuration * 3) {
          if (displayMode !== 'ascii') startPhase('ascii');
        } else {
          setStage('completed');
          setStatus('Secure deletion completed successfully. All data has been permanently erased.');
          clearInterval(timer);
        }
      }
    }, 10);
  }, [startTime, stage, displayMode, startPhase, deletionSuccess, currentTime]);

  useEffect(() => {
    if (stage === 'running') {
      const errorTimeout = setTimeout(() => {
        if (Math.random() < ERROR_PROBABILITY && !errorOccurred) {
          setErrorOccurred(true);
          setStatus('ERROR: Memory access violation detected. Retrying...');
          setTimeout(() => {
            setErrorOccurred(false);
            setStatus(statusMessages[displayMode][Math.floor(Math.random() * statusMessages[displayMode].length)]);
          }, 3000);
        }
      }, Math.random() * (deletionSuccess ? ACCELERATED_PHASE_DURATION : BASE_PHASE_DURATION));

      return () => clearTimeout(errorTimeout);
    }
  }, [stage, errorOccurred, displayMode, deletionSuccess]);

  useEffect(() => {
    if (stage === 'running') {
      const updateInterval = setInterval(() => {
        setGrid(prevGrid => {
          const newGrid = [...prevGrid];
          if (scanLine === newGrid.length - 1) {
            newGrid[newGrid.length - 1] = newGrid[newGrid.length - 1].map(() => generateData(displayMode));
            newGrid.unshift(newGrid.pop());
          } else {
            newGrid[scanLine] = newGrid[scanLine].map(() => generateData(displayMode));
          }
          return newGrid;
        });
      }, SCAN_SPEED);
      
      return () => clearInterval(updateInterval);
    }
  }, [scanLine, displayMode, stage]);

  useEffect(() => {
    if (deletionSuccess) {
      const displayTime = ACCELERATED_PHASE_DURATION * 3;
      const timer = setTimeout(() => {
        setStage('completed');
        setStatus('Secure deletion completed successfully. All data has been permanently erased.');
        clearInterval(timer);
      }, displayTime);
    }
  }, [deletionSuccess]);

  useEffect(() => {
    if (stage === 'completed') {
      const cleanup = () => {
        setStage('initializing');
        setStartTime(null);
        setCurrentTime(null);
        setGrid(generateGrid(dimensions.rows, dimensions.cols));
        setProgress(0);
        setErrorOccurred(false);
        setDisplayMode('hex');
        setStatus('');
      };

      return cleanup;
    }
  }, []);

  const formatTime = (ms) => {
    const seconds = Math.floor(ms / 1000);
    const milliseconds = ms % 1000;
    return `${seconds}.${milliseconds.toString().padStart(3, '0')}s`;
  };

  const cleanup = () => {
    setStage('initializing');
    setStartTime(null);
    setCurrentTime(null);
    setGrid(generateGrid(dimensions.rows, dimensions.cols));
    setProgress(0);
    setErrorOccurred(false);
    setDisplayMode('hex');
    setStatus('');
  };

  const handleCancel = () => {
    cleanup();
  };

  

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4">
      <div className="bg-gray-900 rounded-lg shadow-2xl border border-gray-700 w-full max-w-4xl h-full max-h-[80vh] flex flex-col">
        <div className="p-4 border-b border-gray-700 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className={`w-3 h-3 rounded-full ${
              errorOccurred ? 'bg-red-500' : 
              stage === 'completed' ? 'bg-green-500' :
              'bg-blue-500 animate-pulse'
            }`} />
            <h2 className="text-green-500 font-mono text-lg">Secure Data Deletion Protocol</h2>
          </div>
          <div className="text-green-400 font-mono text-sm space-x-4">
            <span>Elapsed: {startTime ? formatTime(currentTime - startTime) : '0.000s'}</span>
          </div>
        </div>
        
        <div className="flex-1 overflow-auto p-4 bg-black font-mono text-xs">
          <div className="h-full">
            {grid.map((line, i) => (
              <div 
                key={i}
                className={`${
                  errorOccurred ? 'text-red-500' :
                  i === scanLine ? 'bg-gray-800 text-green-300' : 
                  i < scanLine ? 'text-gray-600' : 'text-green-500'
                }`}
              >
                {line.join(' ')}
              </div>
            ))}
          </div>
        </div>

        <div className="p-4 border-t border-gray-700 space-y-2">
          <div className="h-2 bg-gray-800 rounded overflow-hidden">
            <div 
              className={`h-full transition-all duration-200 ${
                errorOccurred ? 'bg-red-500' : 'bg-green-500'
              }`}
              style={{ width: `${progress}%` }}
            />
          </div>
          
          <div className="flex justify-between text-xs text-gray-500">
            <span>Phase: {displayMode.toUpperCase()}</span>
            <span>{Math.round(progress)}%</span>
          </div>
          
          <div className="flex justify-between items-center">
            <p className={`font-mono text-sm ${
              errorOccurred ? 'text-red-400' : 'text-green-400'
            }`}>
              {status}
            </p>
            {stage !== 'completed' && (
              <button
                onClick={handleCancel}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded text-sm"
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecureDeleteSimulator;